import {forwardRef, ReactNode, TextareaHTMLAttributes} from 'react'
import {Label} from '@components/ui/label/Label.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {
    StyledTextarea,
    StyledTextareaContainer,
    StyledTextareaGrid,
    StyledTextareaWrapper
} from '@components/commons/textarea/style.ts'
import {useTranslation} from '@/translations/i18n'

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    className?: string
    label?: string
    name?: string
    typeIcon?: ReactNode
    errorMessage?: string
    helpText?: string
    placeholder?: string
    isOptional?: boolean
    isResizable?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            label,
            name,
            typeIcon,
            errorMessage,
            helpText,
            className,
            disabled,
            placeholder,
            isOptional,
            isResizable,
            ...rest
        },
        ref
    ) => {
        const {t} = useTranslation()

        return (
            <StyledTextareaContainer className={className}>
                {label && (
                    <Label htmlFor={name}>
                        {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                    </Label>
                )}
                <StyledTextareaGrid>
                    <StyledTextareaWrapper $hasError={!!errorMessage} $disabled={disabled}>
                        {typeIcon}
                        <StyledTextarea
                            ref={ref}
                            name={name}
                            placeholder={placeholder}
                            disabled={disabled}
                            $isResizable={isResizable}
                            {...rest}
                        />
                    </StyledTextareaWrapper>
                </StyledTextareaGrid>
                {(errorMessage || helpText) && <InputHelpText error={errorMessage} helpText={helpText} />}
            </StyledTextareaContainer>
        )
    }
)

TextArea.displayName = 'TextArea'
